// Per-table CSV export for perf-v2 (#4302).
//
// Output mirrors the Google Ads UI export shape:
//
//   <Report title>
//   <Date range>
//   <column headers>
//   <data rows>
//
// Pure client-side — no API round-trip, no new deps. xlsx (binary) is a
// sub-followup; CSV opens in every spreadsheet tool and is the universal
// format the team already uses for ad-hoc exports.

export interface ExportColumn<T> {
  /** Column header text. */
  header: string;
  /** Function pulling a primitive value out of the row for the cell. */
  value: (row: T) => string | number | null | undefined;
}

export interface ExportMeta {
  /** Report title — first row of the file (e.g. "Campaign report"). */
  title: string;
  /** Date-range string — second row (e.g. "11 May 2026 - 17 May 2026"). */
  dateRange: string;
  /** Optional file-name slug. The actual filename is composed with dates. */
  filenameSlug?: string;
}

/** Properly escape a CSV field — wrap in quotes and double-up embedded quotes. */
function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  // Wrap whenever the field contains a comma, quote, newline, or leading/trailing whitespace.
  if (/[",\n\r]/.test(str) || str !== str.trim()) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build the CSV body. Exported for unit testing without firing a download.
 */
export function buildCsv<T>(rows: T[], columns: ExportColumn<T>[], meta: ExportMeta): string {
  const lines: string[] = [];
  // Three header rows match the Google Ads UI export shape, so the
  // downloaded file looks identical to what the team currently sends.
  lines.push(escapeCsvField(meta.title));
  lines.push(escapeCsvField(meta.dateRange));
  lines.push(columns.map(c => escapeCsvField(c.header)).join(','));
  for (const row of rows) {
    lines.push(columns.map(c => escapeCsvField(c.value(row))).join(','));
  }
  // Trailing newline — many tools tolerate either; this matches Google's.
  return lines.join('\n') + '\n';
}

/** Trigger a CSV download. Pulls the user out of the loaded page; no nav. */
export function downloadCsv<T>(rows: T[], columns: ExportColumn<T>[], meta: ExportMeta, startDate: string, endDate: string): void {
  const body = buildCsv(rows, columns, meta);
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const slug = meta.filenameSlug ?? meta.title.toLowerCase().replace(/\s+/g, '-');
  const filename = `${slug}_${startDate}_${endDate}.csv`;
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revoke so the browser has the URL when it consumes the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
